<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">Avisos al móvil</h2>
        <p class="db-page-subtitle">Envía una notificación a todos los dispositivos que han activado los avisos en la web.</p>
      </div>
      <div class="am-contador" :title="'Dispositivos suscritos'">
        <span class="material-symbols-outlined">devices</span>
        <span v-if="cargando" class="db-spinner"></span>
        <strong v-else>{{ suscritos }}</strong>
        <span class="am-contador-texto">{{ suscritos === 1 ? "dispositivo" : "dispositivos" }}</span>
      </div>
    </header>

    <p v-if="!cargando && !configurado" class="am-aviso">
      <span class="material-symbols-outlined">warning</span>
      Los avisos no están configurados en el servidor: faltan las claves VAPID en el <code>.env</code> de la API
      (ver DESPLIEGUE.md).
    </p>

    <div class="am-layout">
      <section class="db-card">
        <div class="db-card-head">
          <h3 class="db-card-title">Nuevo aviso</h3>
        </div>
        <form class="db-card-body db-form" @submit.prevent="enviar">
          <div class="db-field">
            <label class="db-label" for="am-titulo">
              Título <span class="am-cuenta">{{ titulo.length }}/{{ MAX_TITULO }}</span>
            </label>
            <input id="am-titulo" v-model="titulo" type="text" class="db-input" :maxlength="MAX_TITULO" placeholder="Ej.: Cambio de horario de misas" />
          </div>

          <div class="db-field">
            <label class="db-label" for="am-cuerpo">
              Mensaje <span class="am-cuenta">{{ cuerpo.length }}/{{ MAX_CUERPO }}</span>
            </label>
            <textarea
              id="am-cuerpo"
              v-model="cuerpo"
              class="db-input"
              rows="3"
              :maxlength="MAX_CUERPO"
              placeholder="Ej.: Desde el lunes, la misa de diario pasa a las 19:30 h."
            ></textarea>
          </div>

          <div class="db-form-row">
            <div class="db-field">
              <label class="db-label" for="am-destino">Al tocar el aviso, abrir…</label>
              <select id="am-destino" v-model="destino" class="db-input">
                <option v-for="d in destinos" :key="d.valor" :value="d.valor">{{ d.texto }}</option>
              </select>
            </div>

            <div v-if="destino === 'noticia'" class="db-field">
              <label class="db-label" for="am-noticia">Noticia</label>
              <select id="am-noticia" v-model="noticiaId" class="db-input" @change="rellenarDesdeNoticia">
                <option value="">Elige una noticia…</option>
                <option v-for="n in noticias" :key="n.clave" :value="n.clave">{{ n.etiqueta }}</option>
              </select>
            </div>

            <div v-if="destino === 'otra'" class="db-field">
              <label class="db-label" for="am-ruta">Dirección de la web</label>
              <input id="am-ruta" v-model.trim="rutaLibre" type="text" class="db-input" placeholder="/Eventos?fecha=2026-12-24" />
              <p class="db-hint">Empieza por <code>/</code>: solo páginas de esta web.</p>
            </div>
          </div>

          <label v-if="imagenNoticia" class="db-switch">
            <input v-model="conImagen" type="checkbox" />
            <span class="db-switch-track"></span>
            Incluir la foto de portada de la noticia (Android y ordenador)
          </label>

          <div class="am-acciones">
            <button type="submit" class="db-btn" :disabled="!valido || enviando || !configurado">
              <span v-if="enviando" class="db-spinner"></span>
              <span v-else class="material-symbols-outlined">send</span>
              Enviar a {{ suscritos }} {{ suscritos === 1 ? "dispositivo" : "dispositivos" }}
            </button>
          </div>
        </form>
      </section>

      <!-- Vista previa -->
      <aside class="am-preview-wrap">
        <p class="am-preview-label">Así se verá</p>
        <div class="am-preview">
          <img src="/iconos/icono-192.png" alt="" class="am-preview-icon" />
          <div class="am-preview-text">
            <p class="am-preview-app">Santo Domingo · ahora</p>
            <p class="am-preview-title">{{ titulo || "Título del aviso" }}</p>
            <p class="am-preview-body">{{ cuerpo || "Texto del mensaje…" }}</p>
          </div>
        </div>
        <img v-if="conImagen && imagenNoticia" :src="urlMiniatura(imagenNoticia)" alt="" class="am-preview-img" />

        <ul class="am-consejos">
          <li><span class="material-symbols-outlined">tips_and_updates</span> Mejor 1 o 2 avisos por semana: si son demasiados, la gente los desactiva.</li>
          <li><span class="material-symbols-outlined">short_text</span> Título corto y concreto; el móvil corta los textos largos.</li>
          <li><span class="material-symbols-outlined">phone_iphone</span> En iPhone solo llegan a quien ha añadido la web a su pantalla de inicio.</li>
        </ul>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from "@/directives/axios.js";
import { avisar, avisarError, confirmar } from "@/directives/dbDialogs.js";
import { urlMiniatura } from "@/directives/media.js";

const MAX_TITULO = 80;
const MAX_CUERPO = 200;

export default {
  name: "VistaAvisosMovil",
  data() {
    return {
      MAX_TITULO,
      MAX_CUERPO,
      cargando: true,
      configurado: true,
      suscritos: 0,
      enviando: false,
      titulo: "",
      cuerpo: "",
      destino: "/",
      noticiaId: "",
      rutaLibre: "",
      conImagen: true,
      noticias: [],
      destinos: [
        { valor: "/", texto: "La portada" },
        { valor: "/#horarios", texto: "Los horarios de misa" },
        { valor: "/Eventos", texto: "El calendario de eventos" },
        { valor: "/vistaNoticias", texto: "Las noticias" },
        { valor: "noticia", texto: "Una noticia concreta" },
        { valor: "/Multimedia", texto: "La galería" },
        { valor: "otra", texto: "Otra página de la web" },
      ],
    };
  },
  computed: {
    noticiaSeleccionada() {
      return this.noticias.find((n) => n.clave === this.noticiaId) || null;
    },
    imagenNoticia() {
      return this.destino === "noticia" && this.noticiaSeleccionada ? this.noticiaSeleccionada.imagen : "";
    },
    url() {
      if (this.destino === "noticia") return this.noticiaSeleccionada ? `/noticia?id=${this.noticiaSeleccionada.id}` : "";
      if (this.destino === "otra") return this.rutaLibre;
      return this.destino;
    },
    valido() {
      const urlOk = /^\/(?!\/)/.test(this.url);
      return this.titulo.trim().length >= 3 && this.cuerpo.trim().length >= 3 && urlOk;
    },
  },
  mounted() {
    this.cargarResumen();
    this.cargarNoticias();
  },
  methods: {
    urlMiniatura,
    async cargarResumen() {
      this.cargando = true;
      try {
        const { data } = await axios.get("/avisos/resumen");
        this.suscritos = data.suscritos || 0;
        this.configurado = !!data.configurado;
      } catch (e) {
        avisarError(this, e, "No se pudo consultar cuántos dispositivos hay suscritos.");
      } finally {
        this.cargando = false;
      }
    },
    async cargarNoticias() {
      try {
        const [parroquia, caritas] = await Promise.all([
          axios.get("/noticias/todasNoticiasVisibles"),
          axios.get("/noticiasCaritas/todasNoticiasVisibles").catch(() => ({ data: {} })),
        ]);
        const lista = [
          ...(parroquia.data.todasNoticias || []).map((n) => ({ ...n, id: String(n.id), caritas: false })),
          ...(caritas.data.todasNoticias || []).map((n) => ({ ...n, id: `caritas-${n.id}`, caritas: true })),
        ];
        this.noticias = lista
          .sort((a, b) => String(b.fechaPublicacion).localeCompare(String(a.fechaPublicacion)))
          .slice(0, 40)
          .map((n) => ({
            clave: n.id,
            id: n.id,
            etiqueta: (n.caritas ? "[Cáritas] " : "") + n.titulo,
            titulo: n.titulo,
            resumen: n.resumenNoticia || "",
            imagen: n.imagenPortada || "",
          }));
      } catch (e) {
        this.noticias = [];
      }
    },
    // Al elegir una noticia, propone su título y resumen (se pueden cambiar)
    rellenarDesdeNoticia() {
      const n = this.noticiaSeleccionada;
      if (!n) return;
      if (!this.titulo) this.titulo = n.titulo.slice(0, MAX_TITULO);
      if (!this.cuerpo) this.cuerpo = (n.resumen || "Nueva noticia en la web de la parroquia.").slice(0, MAX_CUERPO);
    },
    async enviar() {
      if (!this.valido || this.enviando) return;
      const ok = await confirmar(
        this,
        `Se enviará «${this.titulo.trim()}» a ${this.suscritos} ${this.suscritos === 1 ? "dispositivo" : "dispositivos"}. Una vez enviado no se puede retirar.`,
        { titulo: "Enviar aviso", ok: "Enviar" }
      );
      if (!ok) return;

      this.enviando = true;
      try {
        const { data } = await axios.post("/avisos/enviar", {
          titulo: this.titulo.trim(),
          cuerpo: this.cuerpo.trim(),
          url: this.url,
          imagen: this.conImagen && this.imagenNoticia ? this.imagenNoticia : null,
        });
        const r = data.resultado || {};
        const detalles = [];
        if (r.eliminados) detalles.push(`${r.eliminados} dispositivos ya no estaban activos y se han quitado de la lista.`);
        if (r.fallidos) detalles.push(`${r.fallidos} no se pudieron entregar ahora.`);
        avisar(this, data.message, "success", "Aviso enviado", detalles);
        this.suscritos = data.suscritos != null ? data.suscritos : this.suscritos;
        this.titulo = "";
        this.cuerpo = "";
        this.noticiaId = "";
      } catch (e) {
        avisarError(this, e, "No se pudo enviar el aviso.");
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.am-contador {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid var(--sd-border);
  border-radius: 999px;
  background: var(--sd-surface);
  color: var(--sd-text);
}

.am-contador .material-symbols-outlined {
  color: var(--sd-primary);
}

.am-contador strong {
  font-size: 1.15rem;
}

.am-contador-texto {
  font-size: 0.88rem;
  color: var(--sd-muted);
}

.am-aviso {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--sd-warning-soft);
  font-size: 0.9rem;
  color: var(--sd-text);
}

.am-aviso .material-symbols-outlined {
  color: var(--sd-warning);
}

.am-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  align-items: start;
  gap: 20px;
}

.am-cuenta {
  font-weight: 400;
  color: var(--sd-muted);
}

.am-acciones {
  display: flex;
  justify-content: flex-end;
}

.am-preview-wrap {
  position: sticky;
  top: 90px;
}

.am-preview-label {
  margin: 0 0 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.am-preview {
  display: flex;
  gap: 12px;
  padding: 14px;
  border-radius: 18px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow-lg);
}

.am-preview-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
}

.am-preview-text {
  min-width: 0;
}

.am-preview-text p {
  margin: 0;
}

.am-preview-app {
  font-size: 0.72rem;
  color: var(--sd-muted);
}

.am-preview-title {
  font-weight: 700;
  color: var(--sd-text);
  overflow-wrap: anywhere;
}

.am-preview-body {
  display: -webkit-box;
  font-size: 0.9rem;
  color: var(--sd-text);
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.am-preview-img {
  display: block;
  width: 100%;
  max-height: 180px;
  margin-top: 8px;
  border-radius: 14px;
  object-fit: cover;
}

.am-consejos {
  display: grid;
  gap: 10px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.86rem;
  color: var(--sd-muted);
}

.am-consejos li {
  display: flex;
  gap: 8px;
}

.am-consejos .material-symbols-outlined {
  font-size: 19px;
  color: var(--sd-gold);
}

@media (max-width: 900px) {
  .am-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .am-preview-wrap {
    position: static;
  }
}
</style>
